<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogTrigger,
        DialogBody,
    } from '@/components/ui/dialog';
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogTrigger,
    } from '@/components/ui/alert-dialog';
    import { computed, ref } from 'vue';
    import { toast } from 'vue-sonner';
    import { useForm, usePage } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import type { SharedData, Employee } from '@/types';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';
    import { useVoucherStore } from '@/stores/voucher';
    import VoucherMarkAsMissingForm from '@/components/vouchers/VoucherMarkAsMissingForm.vue';
    import type { Table } from '@tanstack/vue-table';
    import RemoveButton from '../RemoveButton.vue';
    import { InputRow, DatePicker } from '@/components/ui/form';
    import { Minus } from 'lucide-vue-next';
    import { today, getLocalTimeZone } from '@internationalized/date';
    import { useDateFormat } from '@/composables/useDateFormat';
    import { useMutation } from '@tanstack/vue-query';
    import axios from 'axios';

    const { formatDMYFromIso } = useDateFormat();

    const props = defineProps<{
        employee: Employee;
    }>();

    const { t } = useI18n();

    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);
    const queryClient = useQueryClient();

    const isConfirmationDialogOpen = ref(false);
    const isDialogOpen = ref(false);
    const isCancelDialogOpen = ref(false);

    const form = useForm({
        id: props.employee.id,
        termination_date: props.employee.termination
            ? props.employee.termination.termination_date
            : today(getLocalTimeZone()).toString(),
    });

    const terminate = () => {
        form.post(route('v2.salary.employee.terminate.create'), {
            preserveScroll: true,
            onSuccess: () => {
                form.resetAndClearErrors();
                toast.success(
                    t('termination.success-msg', {
                        name: props.employee.full_name,
                        date: formatDMYFromIso(form.termination_date),
                    }),
                );
                queryClient.invalidateQueries({ queryKey: [QueryKey.EMPLOYEES, companyId] });
                isDialogOpen.value = false;
            },
            onError: (error: any) => {
                toast.error(error.error);
            },
        });
    };

    const showConfirmationDialog = () => {
        isConfirmationDialogOpen.value = true;
    };

    const showCancelDialog = () => {
        isCancelDialogOpen.value = true;
    };

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            axios.post(
                route('v2.salary.employee.terminate.cancel', {
                    employeeId: props.employee.id,
                }),
            ),
        onSuccess: (data) => {
            toast.success(data.data.success || t('forms.general.success'));
            queryClient.invalidateQueries({ queryKey: [QueryKey.EMPLOYEES, companyId] });
            isDialogOpen.value = false;
        },
        onError: (error: any) => {
            toast.error(error.response.data.error || t('forms.system_error'));
        },
    });
</script>

<template>
    <Dialog v-model:open="isDialogOpen">
        <DialogTrigger as-child>
            <Button
                :variant="employee.termination ? 'continue' : 'delete'"
                size="sm"
            >
                {{
                    employee.termination
                        ? t('termination.table.btns.terminated')
                        : t('termination.table.btns.dismiss')
                }}
                <Minus />
            </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-lg">
            <form
                @submit.prevent="terminate"
                class="flex max-h-full flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle>
                        {{ t('termination.js.input_title') }} {{ employee.full_name }}
                    </DialogTitle>
                </DialogHeader>

                <DialogBody>
                    <InputRow
                        :form="form"
                        name="termination_date"
                        label="employee.termination.termination_date"
                    >
                        <DatePicker v-model="form.termination_date" />
                    </InputRow>
                </DialogBody>

                <DialogFooter class="gap-2 border-t sm:justify-center">
                    <Button
                        v-if="employee.termination"
                        @click.prevent="showCancelDialog"
                        variant="delete"
                        size="sm"
                        class="mr-auto"
                    >
                        {{ t('termination.cancel-termination') }}
                    </Button>
                    <DialogClose as-child>
                        <Button
                            variant="outline"
                            size="sm"
                            @click="form.resetAndClearErrors()"
                        >
                            {{ t('forms.cancel') }}
                        </Button>
                    </DialogClose>

                    <Button
                        :disabled="form.processing"
                        :loading="form.processing"
                        size="sm"
                        @click.prevent="showConfirmationDialog"
                    >
                        {{ t('forms.save') }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>

        <AlertDialog v-model:open="isConfirmationDialogOpen">
            <AlertDialogContent class="sm:max-w-md">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {{ t('termination.js.confirm_title') }}
                        {{ employee.full_name }}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {{ t('termination.js.confirm_msg') }} {{ employee.full_name }} pr.
                        {{ formatDMYFromIso(form.termination_date) }}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{{ t('termination.js.no') }}</AlertDialogCancel>
                    <AlertDialogAction
                        @click="terminate"
                        variant="default"
                    >
                        {{ t('termination.js.yes') }}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
            v-if="employee.termination"
            v-model:open="isCancelDialogOpen"
        >
            <AlertDialogContent class="sm:max-w-md">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {{ t('termination.js.undo_title') }}
                        {{ employee.full_name }}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {{ t('termination.js.undo_msg') }}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{{ t('termination.js.no') }}</AlertDialogCancel>
                    <AlertDialogAction
                        @click="mutate"
                        variant="default"
                    >
                        {{ t('termination.js.yes') }}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </Dialog>
</template>
